/**
 * Route guard that restricts a child route to a whitelist of org ids.
 *
 * Mirrors the {@link ProtectedAdminRoute} pattern: if the current
 * organization (resolved via `getOrgIdForFetchExternalWorklist`) is not
 * in the supplied `orgIds`, the user sees the generic `ErrorPage`
 * instead of the protected content. Otherwise the children render as
 * normal.
 *
 * Useful for org-specific worklists, dashboards, or features that
 * should not exist for other tenants.
 *
 * @module OrgOnlyRoute
 */
import React from "react";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import ErrorPage from "../errorPage/errorPage";

/** Props for {@link OrgOnlyRoute}. */
interface OrgOnlyRouteProps {
  /**
   * Org ids permitted to render `children`. Any other org (or an
   * unresolved org id) falls through to the error page.
   */
  orgIds: string[];
  /** Route content rendered when the current org is on the whitelist. */
  children: React.ReactNode;
}

/**
 * Renders `children` only when the current org id is in `orgIds`;
 * otherwise renders the standard error page.
 */
const OrgOnlyRoute: React.FC<OrgOnlyRouteProps> = ({ orgIds, children }) => {
  const currentOrgId = getOrgIdForFetchExternalWorklist();
  if (!orgIds.includes(currentOrgId)) {
    return <ErrorPage />;
  }
  return <>{children}</>;
};

export default OrgOnlyRoute;
